import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { teamMembersAPI, invitationsAPI } from '../../services/api';

const ROLE_BADGES = {
    owner: 'bg-purple-100 text-purple-800',
    admin: 'bg-blue-100 text-blue-800',
    member: 'bg-muted text-foreground',
    viewer: 'bg-green-100 text-green-800',
};

// Translate the raw mailer/SES error into a short, human reason. The raw text
// (e.g. SES "554 ... Email address is not verified") is kept as the tooltip.
function friendlyEmailError(raw) {
    if (!raw) return 'Email delivery failed. Use the invite link below.';
    const s = String(raw);
    if (/not verified|sandbox|failed the check/i.test(s)) {
        return "Email provider can't send to this address yet (sandbox mode). Copy the invite link instead.";
    }
    if (/rate|throttl|limit exceeded|maximum sending/i.test(s)) {
        return 'Sending rate limit reached. Try Resend in a moment, or copy the link.';
    }
    if (/suppress|bounce|complaint/i.test(s)) {
        return 'This address previously bounced or unsubscribed. Copy the invite link instead.';
    }
    if (/authentic|credential|535|530|signature/i.test(s)) {
        return 'Email service misconfigured (auth). Copy the invite link; an admin should check email settings.';
    }
    return 'Email delivery failed. Copy the invite link below.';
}

const TeamsSettings = () => {
    const { user, currentWorkspace } = useAuth();
    const clientId = currentWorkspace?.id || user?.client?.id || user?.client_id;

    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);

    // Invite form. The workspace is the one in the URL (clientId) — no picker.
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('member');
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteMsg, setInviteMsg] = useState({ type: '', text: '' });

    // Action menu
    const [openMenu, setOpenMenu] = useState(null);

    const fetchData = useCallback(async () => {
        if (!clientId) return;
        try {
            const [membersRes, invitationsRes] = await Promise.all([
                teamMembersAPI.list(clientId),
                invitationsAPI.list(),
            ]);
            setMembers(membersRes.data || []);
            setInvitations((invitationsRes.data || []).filter(inv => inv.status === 'pending'));
        } catch (err) {
            console.error('Failed to load team data:', err);
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleInvite = async (e) => {
        e.preventDefault();
        setInviteLoading(true);
        setInviteMsg({ type: '', text: '' });

        try {
            const res = await invitationsAPI.create({
                email: inviteEmail,
                role: inviteRole,
                client_ids: [clientId],
            });
            const failed = res?.data?.email_status === 'failed';
            setInviteMsg({
                type: failed ? 'warning' : 'success',
                text: res?.status?.message || 'Invitation sent!',
            });
            setInviteEmail('');
            setInviteRole('member');
            fetchData();
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.status?.message || 'Failed to send invite.';
            setInviteMsg({ type: 'error', text: msg });
        } finally {
            setInviteLoading(false);
        }
    };

    const handleResend = async (invId) => {
        try {
            const res = await invitationsAPI.resend(invId);
            const failed = res?.data?.email_status === 'failed';
            setInviteMsg({
                type: failed ? 'warning' : 'success',
                text: res?.status?.message || 'Invitation resent!',
            });
            fetchData();
        } catch (err) {
            setInviteMsg({ type: 'error', text: 'Failed to resend invitation.' });
        }
        setOpenMenu(null);
    };

    const handleCopyInviteLink = async (inv) => {
        const url = `${window.location.origin}/accept-invite?token=${inv.token}`;
        try {
            await navigator.clipboard.writeText(url);
            setInviteMsg({ type: 'success', text: 'Invite link copied to clipboard.' });
        } catch (err) {
            // Clipboard API can be blocked (e.g. non-HTTPS) — surface the link to copy manually.
            setInviteMsg({ type: 'warning', text: `Copy this invite link: ${url}` });
        }
        setOpenMenu(null);
    };

    const handleCancelInvite = async (invId) => {
        try {
            await invitationsAPI.cancel(invId);
            fetchData();
        } catch (err) {
            console.error('Failed to cancel invitation:', err);
        }
        setOpenMenu(null);
    };

    const handleChangeRole = async (memberId, newRole) => {
        try {
            await teamMembersAPI.updateRole(clientId, memberId, newRole);
            fetchData();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to update role.';
            setInviteMsg({ type: 'error', text: msg });
        }
        setOpenMenu(null);
    };

    const handleRemoveMember = async (memberId, memberName) => {
        if (!window.confirm(`Remove ${memberName} from this workspace?`)) return;
        try {
            await teamMembersAPI.remove(clientId, memberId);
            fetchData();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to remove member.';
            setInviteMsg({ type: 'error', text: msg });
        }
        setOpenMenu(null);
    };

    if (!clientId) {
        return <div className="p-4 text-muted-foreground">No workspace selected.</div>;
    }

    return (
        <div className="bg-card rounded-lg border border-border">
            <div className="p-4">
                <div className="space-y-8">
                    {/* Invite Member */}
                    <div className="grid grid-cols-3 gap-8">
                        <div className="col-span-1">
                            <h3 className="text-sm font-medium text-foreground">Invite member</h3>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Send an invitation to add a new member to your workspace.
                            </p>
                        </div>
                        <div className="col-span-2">
                            <form onSubmit={handleInvite} className="flex items-end gap-3">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-foreground mb-1">Email address</label>
                                    <input
                                        type="email"
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                        placeholder="colleague@company.com"
                                        className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm"
                                        required
                                    />
                                </div>
                                <div className="w-36">
                                    <label className="block text-sm font-medium text-foreground mb-1">Role</label>
                                    <select
                                        value={inviteRole}
                                        onChange={(e) => setInviteRole(e.target.value)}
                                        className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm"
                                    >
                                        <option value="admin">Admin</option>
                                        <option value="member">Member</option>
                                        <option value="viewer">Viewer</option>
                                    </select>
                                </div>
                                <button
                                    type="submit"
                                    disabled={inviteLoading}
                                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50"
                                >
                                    {inviteLoading ? 'Sending...' : 'Send invite'}
                                </button>
                            </form>
                            {inviteMsg.text && (
                                <div className={`mt-3 rounded-lg px-4 py-2.5 text-sm ${inviteMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : inviteMsg.type === 'warning' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                    {inviteMsg.text}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Pending Invitations */}
                    {invitations.length > 0 && (
                        <div className="grid grid-cols-3 gap-8">
                            <div className="col-span-1">
                                <h3 className="text-sm font-medium text-foreground">Pending invitations</h3>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    Invitations that haven't been accepted yet.
                                </p>
                            </div>
                            <div className="col-span-2">
                                <div className="border border-border rounded-lg overflow-hidden">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-muted/50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Delivery</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Expires</th>
                                                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-card divide-y divide-gray-200">
                                            {invitations.map((inv) => (
                                                <tr key={inv.id}>
                                                    <td className="px-4 py-3 text-sm text-foreground">{inv.email}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${ROLE_BADGES[inv.role]}`}>
                                                            {inv.role}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {inv.email_status === 'failed' ? (
                                                            <div className="max-w-xs">
                                                                <span
                                                                    title={inv.email_error || 'Email delivery failed'}
                                                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-100"
                                                                >
                                                                    Failed
                                                                </span>
                                                                <p className="mt-1 text-xs text-muted-foreground leading-snug">
                                                                    {friendlyEmailError(inv.email_error)}
                                                                </p>
                                                            </div>
                                                        ) : inv.email_status === 'sent' ? (
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                                                Sent
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-muted-foreground">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-muted-foreground">
                                                        {new Date(inv.expires_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm space-x-2">
                                                        <button
                                                            onClick={() => handleCopyInviteLink(inv)}
                                                            className="text-muted-foreground hover:text-foreground text-xs font-medium"
                                                        >
                                                            Copy link
                                                        </button>
                                                        <button
                                                            onClick={() => handleResend(inv.id)}
                                                            className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                                                        >
                                                            Resend
                                                        </button>
                                                        <button
                                                            onClick={() => handleCancelInvite(inv.id)}
                                                            className="text-red-600 hover:text-red-800 text-xs font-medium"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Members List */}
                    <div className="grid grid-cols-3 gap-8">
                        <div className="col-span-1">
                            <h3 className="text-sm font-medium text-foreground">Members</h3>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Manage workspace members and their roles.
                            </p>
                        </div>
                        <div className="col-span-2">
                            {loading ? (
                                <div className="text-muted-foreground text-sm py-4">Loading members...</div>
                            ) : (
                                <div className="border border-border rounded-lg overflow-hidden">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-muted/50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Member</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Workspace</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Joined</th>
                                                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-card divide-y divide-gray-200">
                                            {members.map((member) => (
                                                <tr key={member.id}>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-muted-foreground">
                                                                {member.name?.split(' ').map(n => n[0]).join('') || '?'}
                                                            </div>
                                                            <div className="ml-3">
                                                                <div className="text-sm font-medium text-foreground">
                                                                    {member.name}
                                                                    {member.id === user?.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}
                                                                </div>
                                                                <div className="text-sm text-muted-foreground">{member.email}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                                                        {member.clients && member.clients.length > 0 ? (
                                                            <span title={member.clients.map(c => c.name).join(', ')}>
                                                                {member.clients[0].name}
                                                                {member.clients.length > 1 && (
                                                                    <span className="ml-1 text-xs text-muted-foreground">+{member.clients.length - 1}</span>
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted-foreground">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${ROLE_BADGES[member.role]}`}>
                                                            {member.role}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
                                                        {new Date(member.joined_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm relative">
                                                        {!member.is_owner && member.id !== user?.id && (
                                                            <div className="relative inline-block">
                                                                <button
                                                                    onClick={() => setOpenMenu(openMenu === member.id ? null : member.id)}
                                                                    className="text-muted-foreground hover:text-muted-foreground"
                                                                >
                                                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                                                    </svg>
                                                                </button>
                                                                {openMenu === member.id && (
                                                                    <div className="absolute right-0 mt-1 w-44 bg-card border border-border rounded-lg shadow-lg z-10">
                                                                        {member.role !== 'admin' && (
                                                                            <button
                                                                                onClick={() => handleChangeRole(member.id, 'admin')}
                                                                                className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted/50"
                                                                            >
                                                                                Make Admin
                                                                            </button>
                                                                        )}
                                                                        {member.role !== 'member' && (
                                                                            <button
                                                                                onClick={() => handleChangeRole(member.id, 'member')}
                                                                                className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted/50"
                                                                            >
                                                                                Make Member
                                                                            </button>
                                                                        )}
                                                                        {member.role !== 'viewer' && (
                                                                            <button
                                                                                onClick={() => handleChangeRole(member.id, 'viewer')}
                                                                                className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted/50"
                                                                            >
                                                                                Make Viewer
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            onClick={() => handleRemoveMember(member.id, member.name)}
                                                                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                                                        >
                                                                            Remove
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamsSettings;
